import type { WaveDefinition } from "../domain/types";

const g = (
  enemyId: string,
  count: number,
  spawnIntervalMs: number,
  delayBeforeMs = 0,
  delayAfterMs = 500,
) => ({ enemyId, count, spawnIntervalMs, delayBeforeMs, delayAfterMs });

const STANDARD_WAVES: readonly WaveDefinition[] = Object.freeze([
  { number: 1, name: "First Footfalls", groups: [g("footman", 8, 850)], completionReward: 55 },
  { number: 2, name: "Marching Orders", groups: [g("footman", 15, 620)], completionReward: 65 },
  { number: 3, name: "Swift Pennants", groups: [g("scout", 12, 520)], completionReward: 75 },
  {
    number: 4,
    name: "Two Companies",
    groups: [g("footman", 10, 640), g("scout", 10, 480, 200)],
    completionReward: 85,
  },
  {
    number: 6,
    name: "Broken Formation",
    groups: [g("militia", 22, 260), g("shield-bearer", 5, 900)],
    completionReward: 125,
  },
  {
    number: 7,
    name: "The Royal Guard",
    groups: [g("guard", 6, 1150), g("standard-bearer", 2, 1500)],
    completionReward: 150,
  },
  {
    number: 8,
    name: "Packed Ranks",
    groups: [g("footman", 24, 220), g("splitter", 8, 620), g("regenerator", 4, 950)],
    completionReward: 185,
    modifier: "dense",
  },
  {
    number: 9,
    name: "Sky and Shadow",
    groups: [g("shadow-runner", 8, 650), g("winged-raider", 8, 600)],
    completionReward: 230,
  },
]);

const BOSS_ROTATION = ["siege", "war-mammoth", "crown-champion"] as const;

function buildMinibossWave(number: number, cycle: number): WaveDefinition {
  return Object.freeze({
    number,
    name: `Miniboss: The Gilded Raider ${cycle + 1}`,
    groups: Object.freeze([
      g("scout", 4 + cycle * 2, Math.max(220, 430 - cycle * 20)),
      g("gilded-raider", 1, 0, 600, 900),
    ]),
    completionReward: 140 + cycle * 30,
    modifier: "miniboss",
  });
}

function buildBossWave(number: number, cycle: number): WaveDefinition {
  const bossId = BOSS_ROTATION[cycle % BOSS_ROTATION.length]!;
  const bossNames: Readonly<Record<(typeof BOSS_ROTATION)[number], string>> = {
    siege: "Siege Engine",
    "war-mammoth": "War Mammoth",
    "crown-champion": "Crown Champion",
  };
  return Object.freeze({
    number,
    name: `Boss: ${bossNames[bossId]} ${cycle + 1}`,
    groups: Object.freeze([
      g("guard", 4 + cycle * 2, Math.max(300, 650 - cycle * 25)),
      g(bossId, 1, 0, 900, 1_400),
    ]),
    completionReward: 360 + cycle * 90,
    modifier: "boss",
  });
}

function buildStandardWave(number: number, cycle: number): WaveDefinition {
  const position = number % 10;
  const templateNumber = position === 0 ? 9 : position;
  const template =
    STANDARD_WAVES.find((wave) => wave.number === templateNumber) ?? STANDARD_WAVES[0]!;
  const countMultiplier = 1 + cycle * 0.16;
  const intervalMultiplier = Math.max(0.48, 1 - cycle * 0.055);
  return Object.freeze({
    ...template,
    number,
    name: cycle === 0 ? template.name : `${template.name} ${cycle + 1}`,
    groups: Object.freeze(
      template.groups.map((group) => {
        const enemyId =
          number < 20 && (group.enemyId === "shadow-runner" || group.enemyId === "winged-raider")
            ? "scout"
            : group.enemyId;
        return Object.freeze({
          ...group,
          enemyId,
          count: Math.max(1, Math.round(group.count * countMultiplier)),
          spawnIntervalMs: Math.max(120, Math.round(group.spawnIntervalMs * intervalMultiplier)),
        });
      }),
    ),
    completionReward: Math.round(template.completionReward * (1 + cycle * 0.08)),
  });
}

export function buildWaveSet(totalWaves: number): readonly WaveDefinition[] {
  return Object.freeze(
    Array.from({ length: totalWaves }, (_, index) => {
      const number = index + 1;
      const cycle = Math.floor(index / 10);
      if (number % 10 === 0) return buildBossWave(number, cycle);
      if (number % 10 === 5) return buildMinibossWave(number, cycle);
      return buildStandardWave(number, cycle);
    }),
  );
}

export const WAVES: readonly WaveDefinition[] = buildWaveSet(10);

export const WAVE_SETS: Readonly<Record<string, readonly WaveDefinition[]>> = Object.freeze({
  easy: buildWaveSet(50),
  medium: buildWaveSet(75),
  hard: buildWaveSet(100),
});
